import { WorkerType } from "@/constants/workerType";
import ImportCsv from "@/features/import-csv";
import type { ExportApiUrl } from "@/features/import-csv/types";
import { ImportCsvSearchSchema } from "@/features/import-csv/zod-schemas";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { match } from "ts-pattern";
import useBoardMemberTableRows from "./-board-member/useBoardMemberTableRows";
import useCollaboratorTableRows from "./-collaborator/useCollaboratorTableRows";
import useContractorTableRows from "./-contractor/useContractorTableRows";
import useEmployeeTableRows from "./-employee/useEmployeeTableRows";
import useWorkerTypeParam from "./-hooks/useWorkerType";

const PersonImportCsv = () => {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { workerType } = useWorkerTypeParam();

  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.person.importCsv.workerType",
  });

  const { t: tCompleteDialog } = useTranslation(defaultNS, {
    keyPrefix: "pages.person.importCsv.completeDialog",
  });

  const title = match(workerType)
    .with(WorkerType.EMPLOYEE, () => t("title.employee"))
    .with(WorkerType.CONTRACTOR, () => t("title.contractor"))
    .with(WorkerType.BOARD_MEMBER, () => t("title.boardMember"))
    .with(WorkerType.COLLABORATOR, () => t("title.collaborator"))
    .exhaustive();

  const useTableRows = match(workerType)
    .with(WorkerType.EMPLOYEE, () => useEmployeeTableRows)
    .with(WorkerType.CONTRACTOR, () => useContractorTableRows)
    .with(WorkerType.BOARD_MEMBER, () => useBoardMemberTableRows)
    .with(WorkerType.COLLABORATOR, () => useCollaboratorTableRows)
    .exhaustive();

  const exportApiUrl = match<keyof typeof WorkerType, ExportApiUrl>(workerType)
    .with(WorkerType.EMPLOYEE, () => "/v2/people-csv/employee")
    .with(WorkerType.CONTRACTOR, () => "/v2/people-csv/contractor")
    .with(WorkerType.BOARD_MEMBER, () => "/v2/people-csv/board-member")
    .with(WorkerType.COLLABORATOR, () => "/v2/people-csv/collaborator")
    .exhaustive();

  const exportFilename = match(workerType)
    .with(WorkerType.EMPLOYEE, () => t("filename.employee"))
    .with(WorkerType.CONTRACTOR, () => t("filename.contractor"))
    .with(WorkerType.BOARD_MEMBER, () => t("filename.boardMember"))
    .with(WorkerType.COLLABORATOR, () => t("filename.collaborator"))
    .exhaustive();

  const templateTitle = match(workerType)
    .with(WorkerType.EMPLOYEE, () => t("prepareCsvFile.templateTitle.employee"))
    .with(WorkerType.CONTRACTOR, () =>
      t("prepareCsvFile.templateTitle.contractor"),
    )
    .with(WorkerType.BOARD_MEMBER, () =>
      t("prepareCsvFile.templateTitle.boardMember"),
    )
    .with(WorkerType.COLLABORATOR, () =>
      t("prepareCsvFile.templateTitle.collaborator"),
    )
    .exhaustive();

  const unit = match(workerType)
    .with(WorkerType.EMPLOYEE, () => t("unit.employees"))
    .with(WorkerType.CONTRACTOR, () => t("unit.contractors"))
    .with(WorkerType.BOARD_MEMBER, () => t("unit.boardMembers"))
    .with(WorkerType.COLLABORATOR, () => t("unit.collaborators"))
    .exhaustive();

  const updateExisting = match(workerType)
    .with(WorkerType.EMPLOYEE, () =>
      t("prepareCsvFile.updateExisting.employee"),
    )
    .with(WorkerType.CONTRACTOR, () =>
      t("prepareCsvFile.updateExisting.contractor"),
    )
    .with(WorkerType.BOARD_MEMBER, () =>
      t("prepareCsvFile.updateExisting.boardMember"),
    )
    .with(WorkerType.COLLABORATOR, () =>
      t("prepareCsvFile.updateExisting.collaborator"),
    )
    .exhaustive();

  return (
    <ImportCsv
      baseLink={`/person/import-csv/${workerType}`}
      baseTitle={title}
      exportApiUrl={exportApiUrl}
      exportFilename={exportFilename}
      templateMessage={{
        templateTitle,
        updateExisting,
        completeLinkButton: tCompleteDialog("button"),
      }}
      completeLinkUrl="/people/workforce"
      unit={unit}
      search={search}
      navigate={navigate}
      useTableRows={useTableRows}
    />
  );
};

export const Route = createFileRoute("/_staff/person/import-csv/$workerType")({
  validateSearch: zodValidator(ImportCsvSearchSchema),
  component: PersonImportCsv,
});
